import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";
import {
  firstInvalidPageIndex,
  fromWorkflowSections,
  sortPages,
  stepSlugs,
  toSubmissionFields,
  validationErrorsOf,
  valuesFromPages,
} from "../../services/dynamicForms/workflowAdapter";
import useValidateWorkflow from "./useValidateWorkflow";
import useErrorModal from "../useErrorModal";
import { HTTP_STATUS_CODES } from "../../constants/api";
import { SEND_MONEY_WORKFLOW_STEPS } from "../../constants/enums";

// types
import type { SendMoneyWorkflow } from "../../types/sendMoney";
import type { SendMoneyReview, SendMoneyWorkflowValidationErrors } from "../../types/api/sendMoney";

type FormValues = Record<string, unknown>;

interface UseWorkflowNavigationArgs {
  workflow: SendMoneyWorkflow;
}

const stepPath = (slug: string): string =>
  generatePath(ROUTES.SEND_MONEY.WORKFLOW, { step: slug });

/**
 * Owns a server-composed workflow while it is being navigated: ordered pages,
 * values accumulated across pages, server validation, and the Review step.
 * The current page is the `:step` URL param — a slug of the page title — so
 * every page has its own readable route. A 226 never changes page titles, so
 * step URLs are fixed for the session.
 *
 * Continue pushes the next step; back is plain history.goBack. The last page's
 * Continue submits every field: a 200 holds the review and pushes the `review`
 * step; a 226 swaps in the returned page content and moves back through the
 * existing history entries to the first page with a server error.
 *
 * Review is only reachable straight after a successful validate. Leaving it
 * discards the review, so returning to it always requires a fresh validate.
 */
const useWorkflowNavigation = ({ workflow }: UseWorkflowNavigationArgs) => {
  const history = useHistory();
  const { step } = useParams<{ step: string }>();
  const { loading: submitting, validateWorkflow } = useValidateWorkflow();
  const openErrorModal = useErrorModal();

  const [pages, setPages] = useState(() => sortPages(workflow.pages));
  const [slugs] = useState(() => stepSlugs(sortPages(workflow.pages)));
  const [revision, setRevision] = useState(0);
  const [editedFields, setEditedFields] = useState<ReadonlySet<string>>(() => new Set());
  const [review, setReview] = useState<SendMoneyReview | null>(null);
  const valuesRef = useRef<FormValues>(valuesFromPages(workflow.pages));
  const reachedSlugs = useRef<Set<string>>(new Set([slugs[0]]));
  const prevStep = useRef(step);

  const isReview = step === SEND_MONEY_WORKFLOW_STEPS.REVIEW && review !== null;
  const requestedIndex = slugs.indexOf(step);
  const isReachable = isReview || (requestedIndex !== -1 && reachedSlugs.current.has(step));
  const index = isReview ? pages.length - 1 : isReachable ? requestedIndex : 0;
  const page = pages[index];

  useEffect(() => {
    if (!isReachable) history.replace(stepPath(slugs[0]));
  }, [isReachable, slugs, history]);

  // Leaving Review discards it — values may change before the next validate.
  useEffect(() => {
    if (prevStep.current === SEND_MONEY_WORKFLOW_STEPS.REVIEW && step !== SEND_MONEY_WORKFLOW_STEPS.REVIEW) setReview(null);
    prevStep.current = step;
  }, [step]);

  const sections = useMemo(() => fromWorkflowSections(page.sections), [page]);

  // A server error is shown until the user edits that field or the next submit replaces it.
  const serverErrors = useMemo(() => {
    const errors = validationErrorsOf(page);
    return Object.keys(errors)
      .filter((name) => !editedFields.has(name))
      .reduce<Record<string, string>>((acc, name) => {
        acc[name] = errors[name];
        return acc;
      }, {});
  }, [page, editedFields]);

  const updateValues = useCallback((values: FormValues, changedName: string) => {
    valuesRef.current = { ...valuesRef.current, ...values };
    setEditedFields((prev) => (prev.has(changedName) ? prev : new Set(prev).add(changedName)));
  }, []);

  const next = useCallback(
    async (values: FormValues) => {
      valuesRef.current = { ...valuesRef.current, ...values };

      if (index < pages.length - 1) {
        const nextSlug = slugs[index + 1];
        reachedSlugs.current.add(nextSlug);
        history.push(stepPath(nextSlug));
        return;
      }

      const fields = toSubmissionFields(pages, valuesRef.current);

      // Saved recipient: no operator on the workflow — the quote identifies the account.
      const body = workflow.operatorId === undefined
        ? { quoteId: workflow.quoteId, fields }
        : { quoteId: workflow.quoteId, operatorId: workflow.operatorId, fields };

      const response = await validateWorkflow(body);
      if (!response) return;

      if (response.status !== HTTP_STATUS_CODES.VALIDATION_ERROR) {
        // Set before the push so the review step is reachable on arrival.
        setReview(response.result as SendMoneyReview);
        history.push(stepPath(SEND_MONEY_WORKFLOW_STEPS.REVIEW));
        return;
      }

      // 226: move to the first page with errors; errors render under their fields
      const nextPages = sortPages((response.result as SendMoneyWorkflowValidationErrors).pages);
      const invalidIndex = firstInvalidPageIndex(nextPages);

      // 226 with no field-level error: nothing to show inline — generic error modal.
      if (invalidIndex === -1) {
        openErrorModal();
        return;
      }

      valuesRef.current = { ...valuesRef.current, ...valuesFromPages(nextPages) };
      setPages(nextPages);
      setEditedFields(new Set());
      setRevision((r) => r + 1);
      // Move back through existing entries so history keeps flow order.
      if (invalidIndex < index) history.go(invalidIndex - index);
    },
    [index, pages, slugs, history, validateWorkflow, openErrorModal, workflow.quoteId, workflow.operatorId]
  );

  return {
    page,
    sections,
    initialValues: valuesRef.current,
    serverErrors,
    /** remount key: new page or new server response re-seeds the form */
    formKey: `${revision}-${page.sequenceId}`,
    submitting,
    next,
    updateValues,
    /** set only while on the review step */
    review: isReview ? review : null,
  };
};

export default useWorkflowNavigation;